import { Form, FormProps, FormRenderProps } from 'react-final-form';
import { Link as RouterLink } from 'react-router-dom';
import { Box, lighten } from '@mui/material';

import { ConfirmDialog, LinearProgress, Link, Typography } from '@occc/ui-design-system';
import { routeCreator } from '@occc-app/routes/Campaign/utils';

import { CampaignSelect } from '@occc-app/routes/CampaignComponents/components/CampaignSelect';
import { CampaignComponentCopyDialogFormValues } from './types';

type CampaignComponentConfirmCopyDialogProps = {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  componentName: string;
  componentId: string;
  initialValues: CampaignComponentCopyDialogFormValues;
  onConfirm: (values: CampaignComponentCopyDialogFormValues) => void;
};

export const CampaignComponentConfirmCopyDialog = ({
  open,
  onClose,
  loading,
  componentName,
  componentId,
  initialValues,
  onConfirm,
}: CampaignComponentConfirmCopyDialogProps) => {
  const renderForm: FormProps<CampaignComponentCopyDialogFormValues>['render'] = ({
    handleSubmit,
  }: FormRenderProps<CampaignComponentCopyDialogFormValues>) => {
    return (
      <ConfirmDialog
        disableEscapeKeyDown={loading}
        confirmButtonText='Copy'
        confirmButtonProps={{
          disabled: loading,
          onClick: handleSubmit,
          type: 'submit',
        }}
        dialogTitle={
          <>
            Create a copy of &nbsp;
            <Typography variant='h6' component='span' fontWeight='bold'>
              {componentName}
            </Typography>
          </>
        }
        open={open}
        onClose={onClose}
      >
        <Box mb={1}>{loading ? <LinearProgress /> : <Box height={4} />}</Box>

        <Typography variant='body1'>
          Select a campaign where you would like to copy this component
        </Typography>

        <Box mt={3}>
          <CampaignSelect disabled={loading} label='Select a Campaign' />
        </Box>

        <Box
          p={2}
          mt={1}
          borderRadius={1}
          sx={({ palette }) => ({ background: lighten(palette.primary.main, 0.9) })}
          component={Typography}
        >
          Or you can create a copy of this component in a &nbsp;
          <Link
            to={routeCreator.create()}
            state={{ componentIdToCopy: componentId }}
            component={RouterLink}
          >
            New Campaign
          </Link>
        </Box>
      </ConfirmDialog>
    );
  };

  return (
    <Form<CampaignComponentCopyDialogFormValues>
      initialValues={initialValues}
      onSubmit={onConfirm}
      render={renderForm}
    />
  );
};
